/**
 * Seller-dashboard API layer + i18n namespace registration (EPIC-006, Phase 7).
 *
 * Registers the `seller-dashboard` i18n namespace (ru/ky/en — all three
 * first-class per ADR-028; RU is fallback). Pattern mirrors
 * `features/admin/api.ts` — we call `i18n.addResourceBundle`, never touch
 * `lib/i18n.ts`.
 *
 * Types mirror the backend Pydantic schemas in
 * `apps/api/garden/features/marketplace/{seller_router,schemas}.py`. Money is
 * `price_kgs` — a Decimal serialised as a JSON **string** (never tiyin); the
 * UI parses once with `parseDecimal` and formats at the display boundary.
 */
import i18n from "@/lib/i18n";
import { ApiError, apiClient, apiFetch } from "@/lib/api-client";
import type {
  AttributeValueIn,
  AttributeValueOut,
} from "@/features/seller-dashboard/attributes/formSchema";

import sellerDashboardEn from "@/locales/en/seller-dashboard.json";
import sellerDashboardKy from "@/locales/ky/seller-dashboard.json";
import sellerDashboardRu from "@/locales/ru/seller-dashboard.json";

export const SELLER_DASHBOARD_NS = "seller-dashboard";

let sellerDashboardI18nRegistered = false;

/**
 * Register the `seller-dashboard` i18n namespace (ru/ky/en) on the shared
 * i18next singleton. Idempotent + init-safe. Called at the top of every
 * seller-dashboard screen render so keys are present regardless of import
 * order. The flag resets while i18n is uninitialised (test `beforeEach`
 * re-runs `initI18n()` after this module first loaded).
 */
export function registerSellerDashboardI18n(): void {
  if (!i18n.isInitialized || typeof i18n.addResourceBundle !== "function") {
    sellerDashboardI18nRegistered = false;
    return;
  }
  if (sellerDashboardI18nRegistered) return;
  i18n.addResourceBundle("ru", SELLER_DASHBOARD_NS, sellerDashboardRu, true, false);
  i18n.addResourceBundle("ky", SELLER_DASHBOARD_NS, sellerDashboardKy, true, false);
  i18n.addResourceBundle("en", SELLER_DASHBOARD_NS, sellerDashboardEn, true, false);
  sellerDashboardI18nRegistered = true;
}

// Best-effort eager registration for the production path.
registerSellerDashboardI18n();

// ─────────────────────────── Frozen enums ──────────────────────────────────

/** Product `stock_state` enum (frozen CHECK — note `out_of_stock`, not `out`). */
export const STOCK_STATES = ["in_stock", "low", "out_of_stock", "unknown"] as const;
export type StockState = (typeof STOCK_STATES)[number];

/** Product `status` enum (frozen CHECK). */
export const PRODUCT_STATUSES = [
  "draft",
  "pending_approval",
  "active",
  "rejected",
  "soft_deleted",
  "hidden_by_seller",
] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

// ─────────────────────────── Types ─────────────────────────────────────────

/**
 * One image on the seller editor surface (mirrors `SellerProductImageDto`).
 *
 * `id` is the real `ProductImage.id` — the dnd key AND the `ordered_image_ids`
 * reorder payload (the reorder endpoint validates by image UUID, not s3-key).
 * `url` / `thumb_url` are composed server-side (best clean variant, else the
 * shared placeholder) so the editor needs no CDN-base config.
 */
export interface SellerProductImage {
  id: string;
  s3_key_original: string;
  s3_key_200: string | null;
  s3_key_600: string | null;
  s3_key_1200: string | null;
  sort_order: number;
  url: string;
  thumb_url: string;
}

/** One product row (mirrors `SellerProductResponse`). Money fields are strings. */
export interface SellerProduct {
  id: string;
  seller_id: string;
  category_id: string;
  name: string;
  description: string | null;
  /** Decimal KGS serialised as a string; null when unset. */
  price_kgs: string | null;
  bulk_price_kgs: string | null;
  bulk_threshold: number | null;
  stock_state: string;
  status: string;
  variant_group_id: string | null;
  /** ISO-8601 timestamp; advanced by the per-table trigger on every update. */
  updated_at: string;
  /** Clean gallery images ordered by `sort_order` (each carries its DB id). */
  images: SellerProductImage[];
  /**
   * PIM Wave P0 Task 11 — populated ONLY on the single-product detail read
   * (`GET /sellers/me/products/{id}`); list/create/update responses fall
   * through to `[]`. Re-GET the detail after a write to see stored values.
   */
  attributes: AttributeValueOut[];
  /** All category ids (primary + extras); `[]` on list rows (detail only). */
  category_ids: string[];
  /** Always mirrors `category_id` server-side. */
  primary_category_id: string | null;
  /**
   * PIM Wave P3 — latest send-back reason (mirrors `SellerProductResponse
   * .review_reason`). "Latest" is not cleared on resubmission/approval, so
   * only render this while `status === "rejected"` — otherwise it may be a
   * stale reason from a product that was later fixed and approved.
   */
  review_reason: string | null;
  /** v0048 workbook properties. Optional so a cached older shape stays
   * assignable; `tags`/`folk_names` are populated on the DETAIL read only. */
  unit?: string | null;
  brand?: string | null;
  article?: string | null;
  article_source?: string | null;
  formal_name?: string | null;
  price_max_kgs?: string | null;
  variant_count?: number;
  tags?: string[];
  folk_names?: string[];
}

/** Cursor-paginated list response (mirrors `ProductListResponse`). */
export interface SellerProductListResponse {
  products: SellerProduct[];
  next_cursor: string | null;
}

/** A photo to attach on PATCH (mirrors `SellerPhotoAttachment`). */
export interface SellerPhotoAttachment {
  s3_key_original: string;
  sort_order: number;
}

/** Create body (mirrors `SellerProductCreate` — no `seller_id`, injected).
 * PIM Wave P2: `attributes` (full-replace when present) + the multi-category
 * pair `category_ids`/`primary_category_id` (both-or-neither). */
export interface SellerProductCreate {
  category_id: string;
  name: string;
  description?: string | null;
  price_kgs?: string | null;
  bulk_price_kgs?: string | null;
  bulk_threshold?: number | null;
  stock_state?: string;
  status?: string;
  attributes?: AttributeValueIn[];
  category_ids?: string[];
  primary_category_id?: string | null;
}

/** Patch body (mirrors `SellerProductUpdate`). `photos` REPLACES the gallery;
 * `attributes` full-replaces the attribute values when present (key absent =
 * untouched). */
export interface SellerProductUpdate {
  category_id?: string;
  name?: string;
  description?: string | null;
  price_kgs?: string | null;
  bulk_price_kgs?: string | null;
  bulk_threshold?: number | null;
  stock_state?: string;
  status?: string;
  photos?: SellerPhotoAttachment[];
  attributes?: AttributeValueIn[];
  category_ids?: string[];
  primary_category_id?: string | null;
}

/** Bulk stock-state result (mirrors `BulkResultResponse`). */
export interface BulkResultResponse {
  affected: string[];
}

/** Variant row (mirrors `VariantResponse`). */
export interface SellerVariant {
  id: string;
  product_id: string;
  variant_attribute_key: string;
  variant_attribute_value: string;
}

/** Vacation status (mirrors `VacationStatusResponse`). */
export interface VacationStatusResponse {
  vacation_mode: boolean;
  /** ISO-8601 string when `vacation_mode` is true, else null. */
  since: string | null;
}

// ────────────────────── PIM Wave P3 — catalog suggestions ──────────────────
// Types mirror `apps/api/garden/features/catalog_schema/schemas.py:601-714`
// (same wire shapes as the admin `features/admin/suggestions/api.ts` copy —
// kept as a separate hand-written copy per the seller/admin persona-file
// convention; see `.superpowers/sdd/task-345-frontend-recon.md` §4).

export type SellerSuggestionKind = "category" | "field" | "option";

/** The raw JSONB a seller submits (mirrors `SuggestionCreate`'s persisted
 * fields on `CatalogSuggestion.payload`). */
export interface SuggestionPayload {
  name: string;
  note?: string | null;
  parent_category_id?: string | null;
  field_type?: string | null;
}

/** One `catalog_suggestions` row (mirrors `SuggestionResponse`). */
export interface SuggestionResponse {
  id: string;
  seller_id: string;
  kind: SellerSuggestionKind;
  payload: SuggestionPayload;
  /** `pending` | `approved` | `declined`. */
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  decline_reason: string | null;
  created_at: string;
}

/** `GET /sellers/me/suggestions` (mirrors `SellerSuggestionListResponse`). */
export interface SellerSuggestionListResponse {
  suggestions: SuggestionResponse[];
  open_count: number;
}

/** Body for `POST /sellers/me/suggestions` (mirrors `SuggestionCreate`,
 * seller-facing subset — no `field_type`; the admin sets it at approval). */
export interface SubmitSuggestionBody {
  kind: SellerSuggestionKind;
  name: string;
  note?: string | null;
  parent_category_id?: string | null;
}

export function submitSuggestion(
  body: SubmitSuggestionBody,
): Promise<SuggestionResponse> {
  return apiClient.post<SuggestionResponse, SubmitSuggestionBody>(
    "/sellers/me/suggestions",
    body,
  );
}

export function listSellerSuggestions(): Promise<SellerSuggestionListResponse> {
  return apiClient.get<SellerSuggestionListResponse>("/sellers/me/suggestions");
}

// ─────────────────────────── Query keys ────────────────────────────────────

export const sellerQueryKeys = {
  products: (filters: Record<string, unknown>) =>
    ["seller", "products", filters] as const,
  product: (id: string) => ["seller", "products", id] as const,
  variants: (productId: string) =>
    ["seller", "products", productId, "variants"] as const,
  vacation: () => ["seller", "vacation"] as const,
} as const;

export const sellerSuggestionKeys = {
  list: () => ["seller", "suggestions", "list"] as const,
} as const;

// ─────────────────────────── Filters ───────────────────────────────────────

export interface ProductListFilters {
  status?: string | null;
  stock_state?: string | null;
  category_id?: string | null;
}

// ─────────────────────────── API fetchers ──────────────────────────────────

export interface ListProductsParams extends ProductListFilters {
  cursor?: string | null;
  limit?: number;
}

export function listSellerProducts(
  params: ListProductsParams,
): Promise<SellerProductListResponse> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.stock_state) query.set("stock_state", params.stock_state);
  if (params.category_id) query.set("category_id", params.category_id);
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiClient.get<SellerProductListResponse>(
    `/sellers/me/products${qs ? `?${qs}` : ""}`,
  );
}

export function getSellerProduct(id: string): Promise<SellerProduct> {
  return apiClient.get<SellerProduct>(`/sellers/me/products/${id}`);
}

export function createSellerProduct(
  body: SellerProductCreate,
): Promise<SellerProduct> {
  return apiClient.post<SellerProduct, SellerProductCreate>(
    "/sellers/me/products",
    body,
  );
}

export function patchSellerProduct(
  id: string,
  body: SellerProductUpdate,
): Promise<SellerProduct> {
  // `apiClient` exposes no `patch` shortcut; go through `apiFetch` directly
  // (still auto-attaches the JWT + Idempotency-Key for the mutating method).
  return apiFetch<SellerProduct, SellerProductUpdate>({
    method: "PATCH",
    path: `/sellers/me/products/${id}`,
    body,
  });
}

export function deleteSellerProduct(id: string): Promise<SellerProduct> {
  return apiClient.delete<SellerProduct>(`/sellers/me/products/${id}`);
}

export function bulkSetStockState(
  productIds: string[],
  stockState: string,
): Promise<BulkResultResponse> {
  return apiClient.post<
    BulkResultResponse,
    { product_ids: string[]; stock_state: string }
  >("/sellers/me/products:bulk-stock-state", {
    product_ids: productIds,
    stock_state: stockState,
  });
}

export function reorderProductPhotos(
  productId: string,
  orderedImageIds: string[],
): Promise<SellerProduct> {
  return apiClient.post<SellerProduct, { ordered_image_ids: string[] }>(
    `/sellers/me/products/${productId}/photos/reorder`,
    { ordered_image_ids: orderedImageIds },
  );
}

export function listSellerVariants(
  productId: string,
): Promise<{ variants: SellerVariant[] }> {
  return apiClient.get<{ variants: SellerVariant[] }>(
    `/sellers/me/products/${productId}/variants`,
  );
}

export function createSellerVariant(
  productId: string,
  key: string,
  value: string,
): Promise<SellerVariant> {
  return apiClient.post<
    SellerVariant,
    { variant_attribute_key: string; variant_attribute_value: string }
  >(`/sellers/me/products/${productId}/variants`, {
    variant_attribute_key: key,
    variant_attribute_value: value,
  });
}

export function deleteSellerVariant(
  productId: string,
  variantId: string,
): Promise<void> {
  return apiClient.delete<void>(
    `/sellers/me/products/${productId}/variants/${variantId}`,
  );
}

export function getVacationStatus(): Promise<VacationStatusResponse> {
  return apiClient.get<VacationStatusResponse>("/sellers/me/vacation");
}

export function putVacationStatus(
  vacationMode: boolean,
): Promise<VacationStatusResponse> {
  return apiClient.put<VacationStatusResponse, { vacation_mode: boolean }>(
    "/sellers/me/vacation",
    { vacation_mode: vacationMode },
  );
}

// ─────────────────────── PIM attribute 422 parsing ─────────────────────────

/** One `{field_slug, code}` item from the structured 422 `fields[]` array. */
export interface PimFieldError {
  field_slug: string;
  code: string;
  message?: string | null;
}

/** The structured PIM 422 envelope: a top-level `pim.*` code + a `fields[]`
 * array (see `catalog_schema/service.py::attribute_field_error_handler`). */
export interface PimError {
  /** `pim.attribute_invalid` (per-value validation) or `pim.required_missing`
   * (the submit gate). */
  code: string;
  fields: PimFieldError[];
}

/**
 * Extract the structured PIM attribute error from a caught error, or `null`
 * when it isn't one (so the caller falls back to a generic toast). Recognises
 * only a 422 `ApiError` whose top-level `code` starts with `pim.`; reads the
 * `fields[]` array off the open-keyed problem body.
 */
export function parsePimError(error: unknown): PimError | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status !== 422) return null;
  const code = error.code;
  if (typeof code !== "string" || !code.startsWith("pim.")) return null;

  const rawFields = (error.problem as Record<string, unknown>)["fields"];
  const fields: PimFieldError[] = [];
  if (Array.isArray(rawFields)) {
    for (const item of rawFields) {
      if (item !== null && typeof item === "object") {
        const rec = item as Record<string, unknown>;
        const fieldSlug = rec["field_slug"];
        const itemCode = rec["code"];
        if (typeof fieldSlug === "string" && typeof itemCode === "string") {
          const message = typeof rec["message"] === "string" ? rec["message"] : null;
          fields.push({ field_slug: fieldSlug, code: itemCode, message });
        }
      }
    }
  }
  return { code, fields };
}
